/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnInit, Signal, computed, effect, inject, viewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { appName } from '@configurations/app-metadata.configurations';
import { Roles } from '@scenario/enums/roles.enum';
import { dispatch, select } from '@store/injectables';
import { UserPreferencesViewModel, retrieveUserPreferences, selectUserPreferencesViewModel } from '@store/scenario/user-preferences/user-preferences-slice';

import { AuthenticationService } from '../security/authentication.service';
import { ThemeService } from '../services/theme.service';
import { retrieveUnitRates } from '@store/scenario/unit-rates/unit-rates-slice';
import { retrieveRegions } from '@store/scenario/regions/regions-slice';
import { retrieveMarketAreas } from '@store/scenario/market-areas/market-areas-slice';
import { retrieveSources } from '@store/scenario/sources/sources-slice';
import { retrieveBasecases } from '@store/scenario/basecases/basecases-slice';
import { retrieveSubtypes } from '@store/scenario/subtypes/subtypes-slice';
import { retrieveWeatherYears } from '@store/scenario/weather-years/weather-years-slice';
import { MatDialog } from '@angular/material/dialog';
import { InsightService } from '@app/services/insight.service';
import { ConnectedUser } from '@app/types/connected-user.interface';
import {
  AppShellComponent,
  FormFieldComponent,
  HeaderAppLogoDirective,
  HeaderAppNameComponent,
  HeaderCenterDirective,
  HeaderComponent,
  HeaderEndDirective,
  HeaderMobileMenuActionComponent,
  HeaderMobileMenuComponent,
  HeaderMobileMenuEndDirective,
  HeaderMobileMenuStartDirective,
  HeaderNavigationComponent,
  HeaderNavigationLinkComponent,
  IconButtonComponent,
  IconComponent,
  ModalComponent,
  ModalService,
  SearchComponent,
  SearchInputDirective,
} from '@totalenergiescode/tds-angular';
import { ToastDisplayerComponent } from '../toast-displayer/toast-displayer.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { PsfSubscriptionModalComponent } from '@app/modal/subscription/psf-subscription-modal.component';
import { PsfHourlyDataRequestModalComponent } from '@app/modal/hourly-data/psf-hourly-data-request-modal.component';

@Component({
  selector: 'app-homepage',
  templateUrl: './homepage.component.html',
  styleUrl: './homepage.component.scss',
  imports: [
    AppShellComponent,
    ToastDisplayerComponent,
    HeaderComponent,
    HeaderAppNameComponent,
    HeaderAppLogoDirective,
    HeaderCenterDirective,
    NgTemplateOutlet,
    HeaderEndDirective,
    IconButtonComponent,
    IconComponent,
    HeaderMobileMenuComponent,
    HeaderMobileMenuStartDirective,
    FormFieldComponent,
    SearchComponent,
    SearchInputDirective,
    HeaderMobileMenuEndDirective,
    HeaderMobileMenuActionComponent,
    HeaderNavigationComponent,
    HeaderNavigationLinkComponent,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    AsyncPipe,
  ],
})
export class HomepageComponent implements OnInit {
  protected themeService = inject(ThemeService);
  private readonly authenticationService = inject(AuthenticationService);
  public connectedUser = this.authenticationService.connectedUser;
  public Roles = Roles;
  appName: string;
  private readonly modalService = inject(ModalService);
  private readonly modal = viewChild.required(ModalComponent);
  private readonly userPreferences: Signal<UserPreferencesViewModel> = select(selectUserPreferencesViewModel);

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly insightService: InsightService,
  ) {
    this.appName = appName;
    effect(() => {
      const display = this.userPreferences().userPreferences.display;
      this.insightService.addPreviewAttributesAwsRum(display, null);
    });
  }

  dispatch = dispatch();

  themeIconButtonLabel = computed(() =>
    this.themeService.theme() === 'light' ? 'Set dark mode' : 'Set light mode',
  );
  themeIconButtonIcon = computed(() =>
    this.themeService.theme() === 'light' ? 'dark_mode' : 'light_mode',
  );

  public logout() {
    this.authenticationService.logout();
  }

  public navigateToSettingsPage() {
    this.router.navigateByUrl('/admin');
  }

  public navigateToHomePage() {
    this.router.navigateByUrl('/');
  }

  ngOnInit(): void {
    this.insightService.initAwsRum();
    this._addInsightServiceAttributes(this.connectedUser.getValue());
    this.dispatch(retrieveRegions());
    this.dispatch(retrieveMarketAreas());
    this.dispatch(retrieveSources());
    this.dispatch(retrieveBasecases());
    this.dispatch(retrieveSubtypes());
    this.dispatch(retrieveUnitRates());
    this.dispatch(retrieveUserPreferences());
    this.dispatch(retrieveWeatherYears());
  }

  close(action?: string) {
    this.modal().closeModal(action);
  }

  openNotificationModal() {
    this.modalService.open({
      content: {
        component: PsfSubscriptionModalComponent,
      },
      onClose: action => console.log(action),
    });
  }

  openHourlyDataRequestModal() {
    this.modalService.open({
      content: {
        component: PsfHourlyDataRequestModalComponent,
      },
    });
  }

  private _addInsightServiceAttributes(connectedUser: ConnectedUser | null) {
    if (connectedUser) {
      this.insightService.addSessionAttributesAwsRum(
        'az_user_id',
        connectedUser.preferred_username,
      );
      if (connectedUser.roles) {
        // Not working, the roles variable is always empty
        const isUser =
          connectedUser.roles.includes(Roles.USER.toString()) ||
          connectedUser.roles.includes(Roles.PC_USER.toString())
            ? 'true'
            : 'false';
        const isAdmin =
          connectedUser.roles.includes(Roles.ADMIN.toString()) ||
          connectedUser.roles.includes(Roles.PC_ADMIN.toString())
            ? 'true'
            : 'false';
        this.insightService.addSessionAttributesAwsRum('isUser', isUser);
        this.insightService.addSessionAttributesAwsRum('isAdmin', isAdmin);
      }
      this.insightService.addSessionAttributesAwsRum(
        'isDeveloper',
        connectedUser.preferred_username.includes('@tdf.hubtotal.net') ? 'true' : 'false',
      );
      this.insightService.recordEvent('login', {});
    }
  }
}
